import { useState, useCallback, useEffect, type DragEvent } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Upload, FileUp, X, CheckCircle, AlertCircle, Loader2, ExternalLink, FolderOpen, FileText, Sparkles } from 'lucide-react'
import { getProjects, getFolders, getUploadUrl, confirmUpload } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'
import PageHeader from '../../components/ui/PageHeader'
import NextStepBanner from '../../components/ui/NextStepBanner'
import EmptyState from '../../components/ui/EmptyState'
import ContextualTip from '../../components/ui/ContextualTip'
import AUResidencyBanner from '../../components/ui/AUResidencyBanner'
import axios from 'axios'

interface UploadFile {
  file: File
  status: 'pending' | 'uploading' | 'embedding' | 'complete' | 'error'
  progress: number
  error?: string
  documentId?: string
}

export default function DocumentUpload() {
  const navigate = useNavigate()
  // UX-009: arriving from inside a project (?project=<id>[&folder=<id>]) preselects it.
  const [searchParams] = useSearchParams()
  const [selectedProject, setSelectedProject] = useState(() => searchParams.get('project') ?? '')
  const [selectedFolder, setSelectedFolder] = useState(() => searchParams.get('folder') ?? '')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [dragOver, setDragOver] = useState(false)

  const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: getProjects })
  const { data: folders } = useQuery({
    queryKey: ['folders', selectedProject],
    queryFn: () => getFolders(selectedProject),
    enabled: !!selectedProject,
  })

  // A ?project= the user cannot see (stale link, someone else's id) is dropped, not trusted.
  useEffect(() => {
    if (projects && selectedProject && !projects.some((p) => p.project_id === selectedProject)) {
      setSelectedProject('')
      setSelectedFolder('')
    }
  }, [projects, selectedProject])
  useEffect(() => {
    if (folders && selectedFolder && !folders.some((f) => f.folder_id === selectedFolder)) {
      setSelectedFolder('')
    }
  }, [folders, selectedFolder])

  const uploadMutation = useMutation({
    mutationFn: async (uploadFile: UploadFile) => {
      const contentType = uploadFile.file.type || 'application/octet-stream'
      const { upload_url, document_id, s3_key, required_headers, upload_id } = await getUploadUrl(
        selectedProject,
        uploadFile.file.name,
        contentType,
        selectedFolder || undefined
      )
      // Retry the S3 PUT up to 3 times on network/5xx errors (not 4xx).
      let lastErr: unknown
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await axios.put(upload_url, uploadFile.file, {
            headers: { 'Content-Type': contentType, ...required_headers },
            onUploadProgress: (e) => {
              if (e.total) {
                const progress = Math.round((e.loaded / e.total) * 90)
                setFiles((prev) =>
                  prev.map((f) => (f.file === uploadFile.file ? { ...f, progress } : f))
                )
              }
            },
          })
          lastErr = null
          break
        } catch (err) {
          lastErr = err
          const status = axios.isAxiosError(err) ? err.response?.status : undefined
          // Don't retry client errors (4xx) — they won't resolve.
          if (status && status >= 400 && status < 500) throw err
          if (attempt < 2) await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)))
        }
      }
      if (lastErr) throw lastErr
      // Show embedding status while confirm runs (includes synchronous embedding)
      setFiles((prev) =>
        prev.map((f) => (f.file === uploadFile.file ? { ...f, status: 'embedding', progress: 95 } : f))
      )
      await confirmUpload({
        upload_id,
        document_id,
        project_id: selectedProject,
        s3_key,
        title: uploadFile.file.name,
        folder_id: selectedFolder || undefined,
        filename: uploadFile.file.name,
        content_type: contentType,
      })
      return document_id
    },
    onSuccess: (docId, uploadFile) => {
      setFiles((prev) =>
        prev.map((f) => (f.file === uploadFile.file ? { ...f, status: 'complete', progress: 100, documentId: docId } : f))
      )
    },
    onError: (err, uploadFile) => {
      setFiles((prev) =>
        prev.map((f) =>
          f.file === uploadFile.file
            ? { ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
            : f
        )
      )
    },
  })

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    setFiles((prev) => {
      // In-queue dedup (T4): skip a file whose name already sits in the queue, so
      // a double drag/select can't enqueue the same file twice. This is a UX
      // guard only — the backend dedup (reuse the existing document_id) is the
      // authoritative protection against duplicate document rows.
      const existingNames = new Set(prev.map((f) => f.file.name))
      const incoming: UploadFile[] = Array.from(newFiles)
        .filter((file) => !existingNames.has(file.name))
        .map((file) => ({ file, status: 'pending' as const, progress: 0 }))
      return [...prev, ...incoming]
    })
  }, [])

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files)
  }

  const handleUploadAll = () => {
    files
      .filter((f) => f.status === 'pending')
      .forEach((f) => {
        setFiles((prev) => prev.map((pf) => (pf.file === f.file ? { ...pf, status: 'uploading' } : pf)))
        uploadMutation.mutate(f)
      })
  }

  const removeFile = (file: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== file))
  }

  const uploadDescription = 'Attach files to a project. We classify supported documents automatically — you can correct the type after upload.'

  // First-run / no-data: you can't add documents without a project. Send the
  // user to create one rather than leaving them stuck at an empty selector.
  if (projects && projects.length === 0) {
    return (
      <div className="max-w-3xl mx-auto">
        <PageHeader title="Add documents" description={uploadDescription} helpKey="upload" />
        <EmptyState
          icon={FolderOpen}
          title="Create a project first"
          description="Documents live inside a project. Create your first project, then come back here to add documents to it."
          action={
            // Opens the New project flow itself. This used to run navigate('/'),
            // which landed back on Home: the page said "create a project" and
            // then offered no way to do it (UX-008).
            <button
              onClick={() => navigate('/new')}
              className="inline-flex items-center gap-2 rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
            >
              Create project
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Add documents"
        description={uploadDescription}
        helpKey="upload"
        banner={
          <NextStepBanner
            stage="How it works"
            message={
              selectedProject
                ? 'Drag files into the box below, then choose Upload All.'
                : 'Pick a project first, then drag your files in.'
            }
          />
        }
      />

      {/* Project/Folder selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-1">Project</label>
          <select
            value={selectedProject}
            onChange={(e) => { setSelectedProject(e.target.value); setSelectedFolder('') }}
            className="w-full px-3 py-2 light-input text-sm outline-none"
          >
            <option value="">Select a project</option>
            {projects ? disambiguateProjectLabels(projects).map((p) => (
              <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
            )) : null}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-1">Folder (optional)</label>
          <select
            value={selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value)}
            disabled={!selectedProject}
            className="w-full px-3 py-2 light-input text-sm outline-none disabled:bg-white/30"
          >
            <option value="">Root folder</option>
            {folders?.map((f) => (
              <option key={f.folder_id} value={f.folder_id}>{f.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6">
        <ContextualTip id="upload-project-required">
          Documents are always added to a project. Pick the project (and an optional folder) they
          belong to before uploading.
        </ContextualTip>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
          dragOver ? 'border-iflow-400 bg-white/30' : 'border-sky-200 bg-white/50'
        }`}
      >
        <FileUp size={40} className="mx-auto text-gray-500 mb-4" />
        <p className="text-sm text-gray-600 mb-2">Drag & drop files here, or</p>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 cursor-pointer transition-colors">
          <Upload size={16} /> Browse Files
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && addFiles(e.target.files)}
          />
        </label>
        <p className="text-xs text-gray-500 mt-3">
          PDF, DOCX, TXT, 3D models (STL/OBJ/STEP/IGES/USDZ), images up to 200MB each.
        </p>
      </div>

      <div className="mt-4">
        <AUResidencyBanner />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">{files.length} file(s)</h3>
            <button
              onClick={handleUploadAll}
              disabled={!selectedProject || files.every((f) => f.status !== 'pending')}
              title={
                !selectedProject
                  ? 'Select a project first'
                  : files.every((f) => f.status !== 'pending')
                  ? 'No files are waiting to upload'
                  : undefined
              }
              className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 disabled:opacity-50 transition-colors"
            >
              Upload All
            </button>
          </div>
          <div className="space-y-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-3 light-card p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{f.file.name}</p>
                  <p className="text-xs text-gray-500">{(f.file.size / 1024).toFixed(1)} KB</p>
                </div>
                {(f.status === 'uploading' || f.status === 'embedding') && (
                  <div className="flex items-center gap-2">
                    {f.status === 'embedding' && <Loader2 size={14} className="text-iflow-500 animate-spin" />}
                    <div className="w-24">
                      <div className="h-1.5 bg-sky-200 rounded-full overflow-hidden">
                        <div className="h-full bg-iflow-500 rounded-full transition-all" style={{ width: `${f.progress}%` }} />
                      </div>
                    </div>
                    {f.status === 'embedding' && <span className="text-[10px] text-iflow-600">Processing document — extracting text and generating connections...</span>}
                  </div>
                )}
                {f.status === 'complete' && (
                  <div className="flex items-center gap-2">
                    {f.documentId && (
                      <button
                        onClick={() => navigate(`/document/${f.documentId}`)}
                        className="flex items-center gap-1 text-xs text-iflow-600 hover:text-iflow-800 transition-colors"
                      >
                        <ExternalLink size={12} />
                        View Document
                      </button>
                    )}
                    <CheckCircle size={18} className="text-green-500" />
                  </div>
                )}
                {f.status === 'error' && (
                  <span className="flex items-center gap-1 text-xs text-red-500">
                    <AlertCircle size={14} /> {f.error}
                  </span>
                )}
                {f.status === 'pending' && (
                  <button onClick={() => removeFile(f.file)} className="text-gray-500 hover:text-gray-600">
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {files.every((f) => f.status === 'complete') && (
            <div className="mt-4 space-y-3">
              <div role="status" className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">All files uploaded successfully!</p>
                  <p className="text-xs text-green-700 mt-1">
                    {selectedFolder
                      ? `Your documents are now in ${folders?.find((f) => f.folder_id === selectedFolder)?.name || 'the selected folder'}. View them to analyse, share, or explore connections.`
                      : `Your documents are now in ${projects?.find((p) => p.project_id === selectedProject)?.name || 'the selected project'}. View them to analyse, share, or explore connections.`
                    }
                  </p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
                <h4 className="text-sm font-semibold text-sky-900 mb-3">What&apos;s next?</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <button
                    data-testid="post-upload-cta-workflow"
                    onClick={() => navigate(`/project/${selectedProject}`)}
                    className="flex items-center gap-2 px-4 py-3 bg-emerald-500 border border-emerald-500 rounded-lg hover:bg-emerald-600 text-sm text-white font-semibold"
                  >
                    <Sparkles size={16} /> Open Workflow
                  </button>
                  <button
                    data-testid="post-upload-cta-folder"
                    onClick={() => navigate(selectedFolder ? `/project/${selectedProject}/folder/${selectedFolder}` : `/project/${selectedProject}`)}
                    className="flex items-center gap-2 px-4 py-3 bg-white border border-sky-200 rounded-lg hover:bg-sky-50 text-sm text-sky-800"
                  >
                    <FolderOpen size={16} /> View in Folder
                  </button>
                  {files.length === 1 && files[0].documentId && (
                    <button
                      data-testid="post-upload-cta-document"
                      onClick={() => navigate(`/document/${files[0].documentId}`)}
                      className="flex items-center gap-2 px-4 py-3 bg-white border border-sky-200 rounded-lg hover:bg-sky-50 text-sm text-sky-800"
                    >
                      <FileText size={16} /> View Document
                    </button>
                  )}
                  <button
                    data-testid="post-upload-cta-more"
                    onClick={() => setFiles([])}
                    className="flex items-center gap-2 px-4 py-3 bg-white border border-sky-200 rounded-lg hover:bg-sky-50 text-sm text-sky-800"
                  >
                    <Upload size={16} /> Upload More
                  </button>
                </div>
                <p className="text-xs text-sky-700 mt-3">
                  Tip: <strong>Open Workflow</strong> opens your project workspace, where you can
                  track progress, generate AI analysis, and share with your team.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
